import { useState, useMemo } from 'react';
import { BarChart2, Users, FileText, Download, RefreshCw, Package } from 'lucide-react';
import { useSubmissions } from '../hooks/useSubmissions';
import { StatCard } from '../components/ui/StatCard';
import { SubmissionsTable } from '../components/admin/SubmissionsTable';
import { ShipAccessPanel } from '../components/admin/ShipAccessPanel';
import { exportSubmissionsToExcel } from '../lib/exportExcel';
import { departmentOptions } from '../lib/validation';
import { useUserRole } from '../components/auth/AdminRoute';

type AdminTab = 'submissions' | 'ship-access';

function getMonthOptions() {
  const options = [{ value: '', label: 'All Time' }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}

const MONTH_OPTIONS = getMonthOptions();
const DEPT_OPTIONS = [
  { value: '', label: 'All Departments' },
  ...departmentOptions.map((d) => ({ value: d, label: d })),
];

export function AdminPage() {
  const { role } = useUserRole();
  // Fall back to 'admin' in dev/non-OIDC environments
  const isAdmin = (role ?? 'admin') === 'admin';

  const [activeTab, setActiveTab] = useState<AdminTab>(isAdmin ? 'submissions' : 'ship-access');
  const [month, setMonth] = useState('');
  const [department, setDepartment] = useState('');

  const filters = useMemo(
    () => ({ month: month || undefined, department: department || undefined }),
    [month, department]
  );

  const { data, loading, error, refetch } = useSubmissions(filters);

  const topDepartment = useMemo(() => {
    if (!data?.summary.byDepartment) return '—';
    const entries = Object.entries(data.summary.byDepartment);
    if (entries.length === 0) return '—';
    return entries.sort((a, b) => b[1] - a[1])[0][0];
  }, [data]);

  const topPerson = useMemo(() => {
    if (!data?.summary.byPerson) return '—';
    const entries = Object.entries(data.summary.byPerson);
    if (entries.length === 0) return '—';
    return entries.sort((a, b) => b[1] - a[1])[0][0].split(' (')[0];
  }, [data]);

  function handleExport() {
    if (!data) return;
    const monthLabel = month
      ? (MONTH_OPTIONS.find((m) => m.value === month)?.label?.replace(' ', '-') ?? month)
      : 'all-time';
    const deptLabel = department ? `-${department.toLowerCase()}` : '';
    exportSubmissionsToExcel(data, `marketing-submissions-${monthLabel}${deptLabel}.xlsx`);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin ? 'Manage submissions and ship request access' : 'Manage ship request access'}
          </p>
        </div>
        {activeTab === 'submissions' && (
          <div className="flex items-center gap-3">
            <button
              onClick={refetch}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={!data || loading}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {isAdmin && (
          <button
            onClick={() => setActiveTab('submissions')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'submissions'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4" />
            Submissions Report
          </button>
        )}
        <button
          onClick={() => setActiveTab('ship-access')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'ship-access'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Package className="w-4 h-4" />
          Ship Request Access
        </button>
      </div>

      {/* Ship Access Tab */}
      {activeTab === 'ship-access' && <ShipAccessPanel />}

      {/* Submissions Tab — admin only */}
      {activeTab === 'submissions' && isAdmin && <>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-4 py-2 border border-border rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-interactive"
        >
          {MONTH_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className="px-4 py-2 border border-border rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-interactive"
        >
          {DEPT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          Failed to load submissions: {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Submissions"
          value={loading ? '—' : (data?.total ?? 0)}
          icon={<FileText className="w-5 h-5 text-primary" />}
        />
        <StatCard
          label="Departments Active"
          value={loading ? '—' : Object.keys(data?.summary.byDepartment ?? {}).length}
          icon={<BarChart2 className="w-5 h-5 text-primary" />}
        />
        <StatCard
          label="Top Department"
          value={loading ? '—' : topDepartment}
          sub={
            !loading && data && topDepartment !== '—'
              ? `${data.summary.byDepartment[topDepartment] ?? 0} submissions`
              : undefined
          }
          icon={<BarChart2 className="w-5 h-5 text-primary" />}
        />
        <StatCard
          label="Most Active User"
          value={loading ? '—' : topPerson}
          icon={<Users className="w-5 h-5 text-primary" />}
        />
      </div>

      {/* Department Breakdown */}
      {data && Object.keys(data.summary.byDepartment).length > 0 && (
        <div className="bg-white border border-border rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            By Department
          </h2>
          <div className="space-y-3">
            {Object.entries(data.summary.byDepartment)
              .sort((a, b) => b[1] - a[1])
              .map(([dept, count]) => {
                const pct = data.total > 0 ? Math.round((count / data.total) * 100) : 0;
                return (
                  <div key={dept}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{dept}</span>
                      <span className="text-gray-500">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            All Submissions
          </h2>
          {data && (
            <span className="text-xs text-muted">
              {data.total} record{data.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <SubmissionsTable rows={data?.rows ?? []} />
        )}
      </div>

      </>}
    </div>
  );
}
